/* Kaguyasu cross-sell 1.0. No app dependency, cart replacement or discount code. */
(() => {
  'use strict';
  const ROLE_ORDER = {
    desk: ['cabinet', 'chair'], reception: ['chair', 'cabinet'],
    conference: ['chair', 'cabinet'], cabinet: ['desk', 'chair'],
    chair: ['desk', 'cabinet'], sofa: ['cabinet', 'chair'], other: ['cabinet', 'chair']
  };
  const COPY = {
    en: {
      heading: 'Complete your space', note: 'Optional additions. Check sizes and finishes before adding.',
      main: 'This item', total: 'Selected total', add: 'Add selected items to cart', choose: 'Select an additional item',
      adding: 'Adding…', added: 'Added to cart', qty: 'Qty', cart: 'View cart', unavailable: 'Choose an available main item first.',
      error: 'Unable to complete the request. Please try again.',
      uncertain: 'Check your cart before trying again. Some items may have been added.',
      changed: 'Price or availability changed. Review your selection before adding.',
      refresh: 'Your market or currency changed. Refresh this page before adding.',
      options: 'Complete the options on the main product first.', quantity: 'Check the selected quantities.',
      desk: 'Desk', reception: 'Reception desk', conference: 'Meeting table', cabinet: 'Storage', chair: 'Chair', sofa: 'Seating', other: 'Additional item'
    },
    de: {
      heading: 'Passend dazu', note: 'Optional erhältlich. Bitte Maße und Ausführungen vor dem Hinzufügen prüfen.',
      main: 'Dieser Artikel', total: 'Gesamtsumme', add: 'Auswahl in den Warenkorb', choose: 'Zusätzlichen Artikel auswählen',
      adding: 'Wird hinzugefügt…', added: 'Im Warenkorb', qty: 'Menge', cart: 'Warenkorb ansehen', unavailable: 'Bitte zuerst eine verfügbare Hauptvariante wählen.',
      error: 'Die Anfrage konnte nicht abgeschlossen werden. Bitte erneut versuchen.',
      uncertain: 'Bitte vor einem erneuten Versuch den Warenkorb prüfen. Artikel wurden möglicherweise hinzugefügt.',
      changed: 'Preis oder Verfügbarkeit geändert. Bitte die Auswahl prüfen.',
      refresh: 'Markt oder Währung geändert. Bitte diese Seite neu laden.',
      options: 'Bitte zuerst die Optionen des Hauptartikels ausfüllen.', quantity: 'Bitte die ausgewählten Mengen prüfen.',
      desk: 'Schreibtisch', reception: 'Empfangstheke', conference: 'Konferenztisch', cabinet: 'Stauraum', chair: 'Stuhl', sofa: 'Sitzmöbel', other: 'Zusätzlicher Artikel'
    },
    fr: {
      heading: 'À associer', note: 'Articles en option. Vérifiez les dimensions et les finitions avant de les ajouter.',
      main: 'Cet article', total: 'Total de la sélection', add: 'Ajouter la sélection au panier', choose: 'Sélectionnez un article supplémentaire',
      adding: 'Ajout en cours…', added: 'Ajouté au panier', qty: 'Qté', cart: 'Voir le panier', unavailable: 'Choisissez d’abord une variante principale disponible.',
      error: 'La demande n’a pas pu être effectuée. Veuillez réessayer.',
      uncertain: 'Vérifiez votre panier avant de réessayer. Certains articles ont peut-être été ajoutés.',
      changed: 'Le prix ou la disponibilité a changé. Vérifiez votre sélection.',
      refresh: 'Le marché ou la devise a changé. Actualisez cette page.',
      options: 'Renseignez d’abord les options de l’article principal.', quantity: 'Vérifiez les quantités sélectionnées.',
      desk: 'Bureau', reception: 'Banque d’accueil', conference: 'Table de réunion', cabinet: 'Rangement', chair: 'Chaise', sofa: 'Assise', other: 'Article supplémentaire'
    }
  };

  function baseRole(product, rules) {
    const item = rules.catalog?.[product.handle];
    if (item?.role) return item.role;
    const tag = (product.tags || []).find(t => /^kcs-role:/.test(t));
    if (tag && ROLE_ORDER[tag.slice(9)]) return tag.slice(9);
    const patterns = [
      ['sofa', /sofa|couch|settee|canapé/i],
      ['reception', /reception|rezeption|empfang|accueil/i],
      ['conference', /conference|meeting|boardroom|konferenz|réunion/i],
      ['cabinet', /cabinet|bookcase|bookshelf|storage|sideboard|schrank|armoire/i],
      ['chair', /chair|stuhl|siège/i],
      ['desk', /desk|table|schreibtisch|bureau/i]
    ];
    for (const text of [product.type || '', product.title || '']) {
      const match = patterns.find(([, pattern]) => pattern.test(text));
      if (match) return match[0];
    }
    return 'other';
  }

  function roleOf(product, variant, rules) {
    const defined = rules.catalog?.[product.handle]?.variantRoles?.[variant.sku];
    if (defined) return defined;
    const base = baseRole(product, rules);
    const text = (variant.options || [variant.title || '']).join(' | ');
    if (/\bdesk\s*\+/i.test(text)) return base;
    if (/-(GZ|CWG|LBG)(?:-|$)/i.test(variant.sku || '')) return 'cabinet';
    if (/-YZ(?:-|$)/i.test(variant.sku || '')) return 'chair';
    for (const value of variant.options || [variant.title || '']) {
      if (/\bdesk\b|cabinet on|side cabinet on|with .*cabinet|without .*cabinet/i.test(value)) continue;
      if (/\bchair\b/i.test(value) && !/armchair/i.test(value)) return 'chair';
      if (/\b(bookcase|file cabinet|cabinet)\b/i.test(value)) return 'cabinet';
    }
    return base;
  }

  function includedRoles(product, variant, rules) {
    const known = rules.catalog?.[product.handle]?.includes?.[variant.sku];
    if (known) return known;
    const text = (variant.options || [variant.title || '']).join(' ');
    return /\bdesk\s*\+/i.test(text) ? ['chair', 'cabinet'].filter(r => new RegExp(r, 'i').test(text)) : [];
  }

  function desiredRoles(product, variant, rules) {
    const included = includedRoles(product, variant, rules);
    return (ROLE_ORDER[roleOf(product, variant, rules)] || ROLE_ORDER.other).filter(r => !included.includes(r));
  }

  function eligible(product, rules) {
    return product && !product.giftCard && product.available !== false &&
      !product.requiresSellingPlan && !product.volumePricing && product.type !== '1' &&
      !rules.catalog?.[product.handle]?.excluded && !(product.tags || []).includes('kcs-exclude');
  }

  function validQuantity(value, rule = {}) {
    const n = Number(value), min = Number(rule.min || 1), step = Number(rule.increment || 1);
    return String(value).trim() !== '' && Number.isSafeInteger(n) && n >= min &&
      (!rule.max || n <= Number(rule.max)) && (n - min) % step === 0;
  }

  function candidateVariants(product, role, current, rules, target = {}) {
    const blocked = [
      ...(rules.excludedPairs?.[current.variant.sku] || []),
      ...(rules.excludedPairs?.[current.product.handle] || [])
    ];
    if (!eligible(product, rules) || blocked.includes(product.handle)) return [];
    return (product.variants || []).filter(v => v.available && Number(v.price) > 0 &&
      String(v.id) !== String(current.variant.id) && !blocked.includes(v.sku) &&
      roleOf(product, v, rules) === role &&
      (!target.sku || v.sku === target.sku) && (!target.variantId || String(v.id) === String(target.variantId)));
  }

  function candidates(current, role, rules, bestsellers, fallback) {
    const output = [];
    const push = (items, source) => (items || []).forEach(item => {
      if (item.role === role && /^[a-z0-9][a-z0-9_-]*$/i.test(item.handle || '')) output.push({...item, source});
    });
    push(rules.overrides?.[current.variant.sku], 'configured');
    push(rules.overrides?.[current.product.handle], 'configured');
    push(rules.sales?.bySku?.[current.variant.sku], 'co-purchase');
    // Same parent product can contain separately purchasable desk/cabinet/chair variants.
    if (candidateVariants(current.product, role, current, rules).length) {
      output.push({handle: current.product.handle, role, source: 'same-product'});
    }
    if (fallback) {
      push(rules.sales?.popular?.[role], 'sales-popularity');
      bestsellers.forEach(p => {
        if (eligible(p, rules) && baseRole(p, rules) === role) output.push({handle: p.handle, role, source: 'collection-best-selling'});
      });
    }
    const seen = new Set();
    return output.filter(item => {
      const key = [item.handle, item.sku || '', item.variantId || ''].join('|');
      if (seen.has(key)) return false;
      seen.add(key); return true;
    });
  }

  function selectedItems(current, rows, quantity, properties = [], sellingPlan = '') {
    if (!current.variant?.available || !validQuantity(quantity, current.variant.quantity_rule)) throw new Error('quantity');
    const items = [{id: String(current.variant.id), quantity: Number(quantity), properties, sellingPlan}];
    const seen = new Set([String(current.variant.id)]);
    for (const row of rows.filter(r => r.selected)) {
      if (!row.variant?.available || !validQuantity(row.quantity, row.variant.quantity_rule)) throw new Error('quantity');
      const id = String(row.variant.id);
      if (seen.has(id)) throw new Error('duplicate');
      seen.add(id);
      items.push({id, quantity: Number(row.quantity), properties: []});
    }
    return items;
  }

  function multipart(items, FormDataClass) {
    const body = new FormDataClass();
    items.forEach((item, i) => {
      body.append(`items[${i}][id]`, item.id);
      body.append(`items[${i}][quantity]`, String(item.quantity));
      if (item.sellingPlan) body.append(`items[${i}][selling_plan]`, item.sellingPlan);
      for (const [key, value] of item.properties || []) body.append(`items[${i}][properties][${key}]`, value);
    });
    return body;
  }

  function totalPrice(current, rows, quantity) {
    return Number(current.variant.price) * Number(quantity) + rows.filter(r => r.selected)
      .reduce((sum, row) => sum + Number(row.variant.price) * Number(row.quantity), 0);
  }

  const api = {baseRole, roleOf, includedRoles, desiredRoles, eligible, validQuantity,
    candidateVariants, candidates, selectedItems, multipart, totalPrice};
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (typeof window === 'undefined' || typeof HTMLElement === 'undefined') return;
  if (customElements.get('kaguyasu-cross-sell')) return;

  const cache = new Map();
  async function read(url, json = false, force = false) {
    const key = String(url);
    if (!force && cache.has(key)) return cache.get(key);
    const task = (async () => {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 10000);
      try {
        const response = await fetch(key, {credentials: 'same-origin', signal: controller.signal,
          cache: force ? 'no-store' : 'default'});
        if (!response.ok) throw new Error('request');
        if (json) return await response.json();
        const text = await response.text();
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const script = doc.querySelector('[data-kcs-feed]');
        if (!script) throw new Error('feed');
        return JSON.parse(script.textContent);
      } finally { clearTimeout(timer); }
    })();
    cache.set(key, task);
    try { return await task; } catch (error) { cache.delete(key); throw error; }
  }

  function element(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  }

  class CrossSell extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.listeners = new AbortController();
      this.rows = [];
      this.generation = 0;
      this.busy = false;
      this.submitted = false;
      this.rules = {catalog: {}, sales: {}, overrides: {}, excludedPairs: {}};
      try {
        this.config = JSON.parse(this.querySelector('[data-kcs-config]').textContent);
        this.copy = COPY[this.config.locale.split('-')[0]] || COPY.en;
        this.product = this.config.product;
        this.info = document.getElementById(`MainProduct-${this.config.section}`);
        this.form = document.getElementById(`product-form-${this.config.section}`);
        if (!this.info || !this.form) return;
        // Move only our own element, outside the original product form.
        const anchor = this.form.closest('.buy-buttons-block') || this.form.closest('product-form-component');
        if (anchor && !anchor.closest('form')) anchor.after(this);
        this.setup();
        const start = () => this.start().catch(() => { this.hidden = true; });
        if ('IntersectionObserver' in window) {
          this.observer = new IntersectionObserver(entries => {
            if (entries.some(e => e.isIntersecting)) { this.observer.disconnect(); start(); }
          }, {rootMargin: '300px'});
          // Observe the visible purchase area, because this component starts hidden.
          this.observer.observe(anchor || this.info);
        } else start();
      } catch (_) { this.hidden = true; }
    }

    disconnectedCallback() {
      // A DOM move during initial mount also invokes this callback.
      queueMicrotask(() => {
        if (this.isConnected) return;
        this.generation += 1;
        this.listeners?.abort();
        this.observer?.disconnect();
        this.unsub?.();
        this.initialized = false;
      });
    }

    setup() {
      const c = this.copy;
      this.querySelector('[data-kcs-heading]').textContent = c.heading;
      this.querySelector('[data-kcs-note]').textContent = c.note;
      this.querySelector('[data-kcs-total-label]').textContent = c.total;
      this.button = this.querySelector('[data-kcs-add]');
      this.status = this.querySelector('[data-kcs-status]');
      this.cartLink = this.querySelector('[data-kcs-cart]');
      this.cartLink.textContent = c.cart;
      this.cartLink.href = this.url('cart');
      const opts = {signal: this.listeners.signal};
      this.button.addEventListener('click', () => this.add(), opts);
      this.info.addEventListener('kagu:variant-start', () => {
        this.variantBusy = true; this.generation += 1; this.updateTotal();
      }, opts);
      this.info.addEventListener('kagu:variant-end', e => {
        this.variantBusy = false;
        this.variantFailed = e.detail?.success === false;
        if (this.started && !this.variantFailed) this.refresh();
        else this.updateTotal();
      }, opts);
      this.info.addEventListener('change', e => {
        if (this.contains(e.target)) return;
        if (this.started && !this.variantBusy) this.refresh();
      }, opts);
      this.info.addEventListener('input', e => {
        if (!this.contains(e.target) && e.target.name === 'quantity') this.updateTotal();
      }, opts);
      this.form.addEventListener('kagu:cart-start', () => { this.mainBusy = true; this.updateTotal(); }, opts);
      this.form.addEventListener('kagu:cart-end', () => { this.mainBusy = false; this.updateTotal(); }, opts);
      // Block simultaneous submits only while our own add is being processed.
      this.form.addEventListener('submit', e => {
        if (this.busy) { e.preventDefault(); e.stopImmediatePropagation(); }
      }, {capture: true, signal: this.listeners.signal});
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsub = subscribe(PUB_SUB_EVENTS.variantChange, ({data}) => {
          if (String(data?.sectionId) !== String(this.config.section)) return;
          const next = data.html?.querySelector(`#Kcs-${CSS.escape(this.config.section)} [data-kcs-config]`);
          if (next) {
            try { this.product = JSON.parse(next.textContent).product; } catch (_) { /* keep current safe data */ }
          }
        });
      }
      this.updateTotal();
    }

    url(path, params = {}) {
      const root = (this.config.root || '/').replace(/\/?$/, '/');
      const url = new URL(root + path, window.location.origin);
      if (url.origin !== window.location.origin) throw new Error('origin');
      Object.entries(params).forEach(([k, v]) => url.searchParams.set(k, v));
      const preview = new URL(window.location.href).searchParams.get('preview_theme_id');
      if (preview) url.searchParams.set('preview_theme_id', preview);
      return url.href;
    }

    assertMarket(data) {
      if (data.currency !== this.config.currency || (data.country && data.country !== this.config.country)) throw new Error('refresh');
    }

    async getProduct(handle, force = false) {
      if (!/^[a-z0-9][a-z0-9_-]*$/i.test(handle)) throw new Error('handle');
      if (!force && handle === this.product.handle) return this.product;
      const result = await read(this.url(`products/${encodeURIComponent(handle)}`, {section_id: 'kaguyasu-cross-sell-feed'}), false, force);
      this.assertMarket(result);
      if (result.kind !== 'product' || result.product?.handle !== handle) throw new Error('product');
      return result.product;
    }

    async start() {
      if (this.started) return;
      this.started = true;
      const result = await read(this.config.rulesUrl, true);
      if (result.version !== 1) throw new Error('rules');
      this.rules = result;
      if (!eligible(this.product, this.rules)) return;
      this.bestsellers = [];
      if (this.config.fallback) {
        // Fetch compact handles only. Prices/variants are fetched for chosen candidates.
        try {
          let url = this.url('collections/all', {sort_by: 'best-selling', section_id: 'kaguyasu-cross-sell-feed'});
          for (let page = 0; page < 3 && url; page += 1) {
            const data = await read(url);
            this.assertMarket(data);
            if (data.kind !== 'collection' || data.sort !== 'best-selling') break;
            this.bestsellers.push(...data.products);
            if (!data.next) break;
            const next = new URL(data.next, window.location.origin);
            if (next.origin !== window.location.origin) break;
            next.searchParams.set('sort_by', 'best-selling');
            next.searchParams.set('section_id', 'kaguyasu-cross-sell-feed');
            const preview = new URL(window.location.href).searchParams.get('preview_theme_id');
            if (preview) next.searchParams.set('preview_theme_id', preview);
            url = next.href;
          }
        } catch (_) { /* exact / same-product recommendations may still work */ }
      }
      await this.refresh(true);
    }

    current() {
      const id = this.form?.querySelector('[name="id"]')?.value;
      const variant = this.product?.variants?.find(v => String(v.id) === String(id));
      return variant ? {product: this.product, variant} : null;
    }

    mainQuantity() {
      return new FormData(this.form).get('quantity') || '1';
    }

    mainReady() {
      const current = this.current();
      const submit = this.form?.querySelector('[name="add"]');
      return current?.variant.available && !this.variantBusy && !this.variantFailed && !this.mainBusy &&
        !submit?.disabled && submit?.getAttribute('aria-disabled') !== 'true';
    }

    async refresh(force = false) {
      if (!this.started || !this.rules.version) return;
      const current = this.current();
      if (!current || !eligible(this.product, this.rules)) { this.hidden = true; return; }
      if (!force && !this.loading && this.lastVariant === String(current.variant.id)) { this.updateTotal(); return; }
      this.lastVariant = String(current.variant.id);
      const generation = ++this.generation;
      this.loading = true;
      this.rows = [];
      this.updateTotal();
      const desired = desiredRoles(this.product, current.variant, this.rules);
      const choices = await Promise.all(desired.map(async role => {
        const list = candidates(current, role, this.rules, this.bestsellers || [], this.config.fallback);
        // Bound storefront work if many products have gone unavailable.
        for (const target of list.slice(0, 12)) {
          if (generation !== this.generation || !this.isConnected) return null;
          try {
            const product = await this.getProduct(target.handle);
            const variants = candidateVariants(product, role, current, this.rules, target);
            if (!variants.length) continue;
            // Explicit SKU recommendations stay restricted to that SKU.
            const variant = variants[0];
            return {product, variants, variant, role, target, selected: false, quantity: Number(variant.quantity_rule?.min || 1)};
          } catch (_) { /* try next eligible candidate */ }
        }
        return null;
      }));
      if (generation !== this.generation || !this.isConnected) return;
      this.loading = false;
      this.rows = choices.filter(Boolean);
      this.renderRows();
      this.hidden = this.rows.length === 0;
      this.updateTotal();
    }

    money(cents) {
      return new Intl.NumberFormat(this.config.locale, {style: 'currency', currency: this.config.currency}).format(Number(cents) / 100);
    }

    renderRows() {
      const list = this.querySelector('[data-kcs-list]');
      list.replaceChildren();
      this.rows.forEach((row, i) => {
        const card = element('div', 'kcs-row');
        card.dataset.source = row.target.source;
        const check = element('input', 'kcs-check'); check.type = 'checkbox';
        check.id = `${this.id}-select-${i}`; check.checked = row.selected;
        const img = element('img', 'kcs-image'); img.width = 62; img.height = 62; img.loading = 'lazy'; img.alt = '';
        const info = element('div', 'kcs-info');
        const label = element('label', 'kcs-label', this.copy[row.role]); label.htmlFor = check.id;
        const link = element('a', 'kcs-title', row.product.title);
        link.title = row.product.title;
        const select = element('select', 'kcs-select');
        select.setAttribute('aria-label', `${this.copy[row.role]} — ${row.product.title}`);
        for (const variant of row.variants) {
          const opt = element('option', '', variant.title === 'Default Title' ? row.product.title : variant.title);
          opt.value = String(variant.id); select.appendChild(opt);
        }
        select.value = String(row.variant.id);
        const selection = element('p', 'kcs-selection');
        const controls = element('div', 'kcs-controls');
        const price = element('span', 'kcs-price');
        const qtyLabel = element('label', 'kcs-qty-label', this.copy.qty);
        const qty = element('input', 'kcs-qty'); qty.type = 'number'; qty.inputMode = 'numeric';
        qtyLabel.appendChild(qty); controls.append(price, qtyLabel);
        const display = () => {
          const v = row.variant;
          const image = v.image || row.product.image;
          if (image && /^(https?:\/\/|\/)/.test(image)) { img.src = image; img.hidden = false; } else img.hidden = true;
          link.href = this.url(`products/${row.product.handle}`, {variant: v.id});
          price.textContent = this.money(v.price);
          selection.textContent = v.title === 'Default Title' ? '' : v.title;
          qty.min = String(v.quantity_rule?.min || 1);
          qty.step = String(v.quantity_rule?.increment || 1);
          if (v.quantity_rule?.max) qty.max = String(v.quantity_rule.max); else qty.removeAttribute('max');
          qty.value = String(row.quantity);
          qty.setAttribute('aria-label', `${this.copy.qty} — ${this.copy[row.role]}`);
        };
        check.addEventListener('change', () => { row.selected = check.checked; this.updateTotal(); });
        select.addEventListener('change', () => {
          row.variant = row.variants.find(v => String(v.id) === select.value);
          row.quantity = Number(row.variant.quantity_rule?.min || 1);
          display(); this.updateTotal();
        });
        qty.addEventListener('input', () => { row.quantity = qty.value; this.updateTotal(); });
        info.append(label, link);
        if (row.variants.length > 1) info.appendChild(select);
        info.append(selection, controls);
        card.append(check, img, info); list.appendChild(card); display();
      });
    }

    updateTotal() {
      if (!this.button) return;
      const current = this.current();
      const main = this.querySelector('[data-kcs-main]');
      main.replaceChildren();
      const quantity = this.mainQuantity();
      if (current) {
        const info = element('div', 'kcs-main-info');
        info.append(element('strong', '', this.copy.main), element('span', '', current.variant.title === 'Default Title' ? current.product.title : current.variant.title));
        main.append(info, element('span', 'kcs-price', `${this.money(current.variant.price)} × ${quantity}`));
      }
      const selected = this.rows.some(r => r.selected);
      const valid = current && validQuantity(quantity, current.variant.quantity_rule) &&
        this.rows.filter(r => r.selected).every(r => validQuantity(r.quantity, r.variant.quantity_rule));
      this.querySelector('[data-kcs-total]').textContent = valid ? this.money(totalPrice(current, this.rows, quantity)) : '—';
      this.button.disabled = this.busy || this.submitted || this.loading || !this.mainReady() || !selected || !valid;
      this.button.textContent = this.busy ? this.copy.adding : this.writeUncertain ? this.copy.cart : this.submitted ? this.copy.added : selected ? this.copy.add : this.copy.choose;
      this.querySelectorAll('.kcs-check, .kcs-select, .kcs-qty').forEach(n => { n.disabled = this.busy || this.submitted || this.loading; });
    }

    properties() {
      const data = new FormData(this.form);
      const out = [];
      for (const [key, value] of data) {
        const match = key.match(/^properties\[(.*)\]$/);
        if (match && !(value instanceof File && value.size === 0)) out.push([match[1], value]);
      }
      this.info.querySelectorAll('[name^="properties["]').forEach(input => {
        if (this.contains(input) || input.disabled || input.form === this.form || (input.form && input.form !== this.form)) return;
        if (['radio', 'checkbox'].includes(input.type) && !input.checked) return;
        const match = input.name.match(/^properties\[(.*)\]$/);
        if (!match) return;
        if (input.type === 'file') {
          if (input.files?.[0]) out.push([match[1], input.files[0]]);
        } else if (input.value) out.push([match[1], input.value]);
      });
      return {properties: out, sellingPlan: data.get('selling_plan') || ''};
    }

    async add() {
      if (this.button.disabled || this.busy || this.submitted) return;
      this.status.textContent = ''; this.cartLink.hidden = true;
      const agreement = this.form.querySelector('[id^="agree_condition-"]');
      if (!this.form.reportValidity() || (agreement && !agreement.checked)) { this.status.textContent = this.copy.options; return; }
      const requiredProperties = [...this.info.querySelectorAll('[name^="properties["][required]')]
        .filter(input => !input.disabled && (!input.form || input.form === this.form));
      if (requiredProperties.some(input => !input.reportValidity())) { this.status.textContent = this.copy.options; return; }
      const properties = this.properties();
      if (properties.sellingPlan) { this.status.textContent = this.copy.options; return; }
      const current = this.current(), quantity = this.mainQuantity();
      const generation = this.generation;
      let sent = false;
      this.busy = true; this.updateTotal();
      try {
        if (!this.mainReady()) throw new Error('unavailable');
        const items = selectedItems(current, this.rows, quantity, properties.properties, properties.sellingPlan);
        if (items.length < 2) throw new Error('choose');
        const cart = await read(this.url('cart.js'), true, true);
        if (cart.currency !== this.config.currency) throw new Error('refresh');
        for (const item of items) {
          const variant = item.id === String(current.variant.id) ? current.variant : this.rows.find(r => String(r.variant.id) === item.id).variant;
          const existing = (cart.items || []).filter(line => String(line.variant_id) === item.id).reduce((sum, line) => sum + Number(line.quantity), 0);
          if (variant.quantity_rule?.max && existing + item.quantity > variant.quantity_rule.max) throw new Error('quantity');
        }
        // Recheck current-market prices, availability and quantity rules before any write.
        const handles = [...new Set([this.product.handle, ...this.rows.filter(r => r.selected).map(r => r.product.handle)])];
        const fresh = new Map(await Promise.all(handles.map(async h => [h, await this.getProduct(h, true)])));
        let changed = false;
        const check = (product, variant, qty) => {
          const p = fresh.get(product.handle), v = p?.variants.find(x => String(x.id) === String(variant.id));
          if (!eligible(p, this.rules) || !v?.available || v.price !== variant.price || !validQuantity(qty, v.quantity_rule)) changed = true;
        };
        check(this.product, current.variant, quantity);
        this.rows.filter(r => r.selected).forEach(r => check(r.product, r.variant, r.quantity));
        if (changed) {
          this.product = fresh.get(this.product.handle) || this.product;
          await this.refresh(true);
          throw new Error('changed');
        }
        if (generation !== this.generation || String(this.current()?.variant.id) !== String(current.variant.id) ||
            String(this.mainQuantity()) !== String(quantity) || !this.mainReady()) throw new Error('changed');
        const body = multipart(items, FormData);
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), 20000);
        sent = true;
        let response, result;
        try {
          response = await fetch(this.url('cart/add.js'), {method: 'POST', credentials: 'same-origin',
            headers: {'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest'}, body, signal: controller.signal});
          result = await response.json();
        } finally { clearTimeout(timer); }
        if (!response.ok || result.status || !Array.isArray(result.items) ||
            !items.every(item => result.items.some(line => String(line.variant_id || line.id) === item.id))) {
          throw new Error('uncertain');
        }
        this.submitted = true;
        this.status.textContent = this.copy.added;
        this.cartLink.hidden = false;
        this.dispatchEvent(new CustomEvent('kcs:added', {bubbles: true, detail: {variantIds: items.map(i => i.id)}}));
        // Use the standard cart page; original cart lines/properties/discounts are preserved.
        window.location.assign(this.url('cart'));
      } catch (error) {
        if (sent) {
          // Never automatically retry a write: a timeout/422 can follow a partial addition.
          this.submitted = true;
          this.writeUncertain = true;
          this.status.textContent = this.copy.uncertain;
          this.cartLink.hidden = false;
        } else this.status.textContent = this.copy[error.message] || this.copy.error;
      } finally {
        this.busy = false; this.updateTotal();
      }
    }
  }
  customElements.define('kaguyasu-cross-sell', CrossSell);
})();
